import Iconify from "src/components/Iconify";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Button, Divider, Link, Stack, Typography } from "@mui/material";
import LoadingButton from "src/components/LoadingButton";
import { RHFRadioGroup, RHFTextField } from "src/components/hook-form";
import { CardNeutral } from "src/components/card";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { CustomerUpsertToken } from "../../hooks/useCreateMutation";
import { confirmDialog } from "src/components/ConfirmDialog";
import { CONTACT_EMAIL } from "src/config/config";
import * as yup from "yup";
import Label from "src/components/Label";

export const emptyHomeCustomerToken = () => ({
    rfid: "",
    isActive: "active",
    validFrom: null,
    validTo: null,
    isUnlimited: true,
    hasBillingProfile: false,
    billingProfile: null,
});

export const emptyHomeCustomerForm = () => ({
    email: "",
    firstName: "",
    lastName: "",
    customerGroup: null,
    active: "active",
    creditLimit: "",
    creditLimitType: "noCredit",
    tokens: [{ ...emptyHomeCustomerToken() }],
});

export const homeCustomerFormSchema = (t: TFunction) =>
    yup.object().shape({
        email: yup
            .string()
            .email(t("shared.validation.email.email"))
            .required(t("shared.validation.email.required")),
        firstName: yup
            .string()
            .max(255, t("shared.validation.firstName.maxLen"))
            .required(t("shared.validation.firstName.required")),
        lastName: yup
            .string()
            .max(255, t("shared.validation.lastName.maxLen"))
            .required(t("shared.validation.lastName.required")),
        customerGroup: yup.string().nullable(true),
        creditLimit: yup.string().nullable(true),
        tokens: yup
            .array()
            .of(
                yup.object({
                    rfid: yup
                        .string()
                        .min(3, t("customer.upsert.validation.rfid.minLen"))
                        .max(255, t("customer.upsert.validation.rfid.maxLen"))
                        .nullable(true),
                })
            )
            .min(1),
    });

const HomeAccountUpsertCustomerAkaUserForm: FC = () => {
    const { t } = useTranslation();
    const { getValues, setValue, control } = useFormContext();

    const handleRfidTokenAdd = () => {
        setValue("tokens", getValues("tokens").concat(emptyHomeCustomerToken()));
    };

    const handleTokenRemove = (index?: number) => {
        confirmDialog(t("customer.upsert.deleteRfidMsg"), () => {
            setValue(
                "tokens",
                getValues("tokens").filter((token: CustomerUpsertToken, i: number) => i !== index)
            );
        });
    };

    const tokens = useWatch({ control, name: "tokens" });

    return (
        <>
            <Stack spacing={3}>
                <Typography variant="section">{t("customer.upsert.yourUser")}</Typography>
                <RHFRadioGroup
                    name="active"
                    options={["active", "disabled"]}
                    getOptionLabel={[
                        <Label color="success" key="1">
                            {t("shared.activity.active")}
                        </Label>,
                        <Label color="error" key="2">
                            {t("shared.activity.disabled")}
                        </Label>,
                    ]}
                    margin="normal"
                    sx={{ mt: -2, mb: -1.5 }}
                />
                <Box
                    sx={{
                        display: "grid",
                        rowGap: 3,
                        columnGap: 3,
                        gridTemplateColumns: {
                            xs: "repeat(1, 1fr)",
                            md: "repeat(2, 1fr)",
                        },
                    }}
                >
                    <RHFTextField
                        name="email"
                        label={t("customer.upsert.email")}
                        placeholder={t("customer.upsert.emailPl")}
                    />
                    <Box sx={{ display: { xs: "none", md: "block" } }} />
                    <RHFTextField
                        name="firstName"
                        label={t("customer.upsert.firstName")}
                        placeholder={t("customer.upsert.firstNamePl")}
                    />
                    <RHFTextField
                        name="lastName"
                        label={t("customer.upsert.lastName")}
                        placeholder={t("customer.upsert.lastNamePl")}
                    />
                </Box>
            </Stack>
            <Divider sx={{ my: 4 }} />
            <Stack>
                <Box sx={{ mt: 0, mb: 3 }}>
                    <Typography variant="section">{t("customer.upsert.rfidCards")}</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t("customer.upsert.rfidCardsHint"),
                            }}
                        />
                        -{" "}
                        <Link
                            underline="always"
                            color="textSecondary"
                            href={`mailto:${CONTACT_EMAIL}`}
                        >
                            {t("customer.upsert.rfidCardsMailBtn")}
                        </Link>
                    </Typography>
                </Box>
                {tokens.length > 0 && (
                    <Stack spacing={3}>
                        {tokens.map((_: CustomerUpsertToken, i: number) => {
                            return (
                                <CardNeutral
                                    key={i}
                                    onClose={
                                        i > 0 || getValues("tokens").length > 1
                                            ? () => handleTokenRemove(i)
                                            : undefined
                                    }
                                >
                                    <Stack spacing={3}>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                rowGap: 3,
                                                columnGap: 3,
                                                gridTemplateColumns: {
                                                    xs: "repeat(1, 1fr)",
                                                    md: "repeat(2, 1fr)",
                                                },
                                            }}
                                        >
                                            <RHFTextField
                                                name={`tokens.${i}.rfid`}
                                                label={t("customer.upsert.rfidTag")}
                                                tooltip={t("customer.upsert.rfidTagTooltip")}
                                                placeholder={t("customer.upsert.rfidTagPl")}
                                            />
                                        </Box>
                                    </Stack>
                                </CardNeutral>
                            );
                        })}
                    </Stack>
                )}
                <Box sx={{ mt: 2 }}>
                    <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<Iconify icon={"eva:plus-fill"} />}
                        onClick={handleRfidTokenAdd}
                    >
                        {t("customer.upsert.addRfid")}
                    </Button>
                </Box>
            </Stack>
            <Divider sx={{ my: 4 }} />
            <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ mb: 1 }}>
                <LoadingButton size="large" type="submit" variant="contained">
                    {t("customer.upsert.saveUser")}
                </LoadingButton>
            </Stack>
        </>
    );
};

export default HomeAccountUpsertCustomerAkaUserForm;
